import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { addSchemaToPage, removePageSchemas, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seo';

const ContactoPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - Presupuesto de Pladur en Badalona | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con PLADUREX para tu proyecto de pladur en Badalona. Presupuesto sin compromiso en 24h. ☎ 629 501 941 o WhatsApp.');
    }

    // Add Schema.org JSON-LD
    const schemas = [
      addSchemaToPage(getBreadcrumbSchema([
        { name: 'Inicio', url: '/' },
        { name: 'Contacto', url: '/contacto' }
      ])),
      addSchemaToPage(getWebPageSchema(
        'Contacto - PLADUREX',
        'Contacta con PLADUREX para tu proyecto de pladur en Badalona. Presupuesto sin compromiso en 24h.',
        '/contacto'
      ))
    ];

    return () => {
      removePageSchemas();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validar textarea
    const mensaje = formData.get('mensaje') as string;
    if (mensaje && mensaje.length > 500) {
      alert('El mensaje no puede superar los 500 caracteres');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d61edp950i17hner1ktg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-36 md:pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
            >
              <span className="text-orange-primary text-sm font-bold">Contacto</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-text-primary mb-6"
            >
              Solicita tu presupuesto sin compromiso
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed"
            >
              Te respondemos en menos de 24 horas con un presupuesto detallado
            </motion.p>
          </div>
        </section>

        {/* Contact Section */}
        <section ref={formRef} className="py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={formInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-extrabold text-text-primary mb-8">
                  Información de contacto
                </h2>

                <div className="space-y-6 mb-10">
                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-orange-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-text-primary font-bold mb-1">Teléfono</h3>
                      <a
                        href="tel:+34629501941"
                        className="text-text-secondary hover:text-orange-primary transition-colors duration-300 text-lg"
                      >
                        +34 629 501 941
                      </a>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#25D366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-whatsapp-line text-[#25D366] text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-text-primary font-bold mb-1">WhatsApp</h3>
                      <a
                        href="https://wa.me/34629501941"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-text-secondary hover:text-orange-primary transition-colors duration-300 text-lg"
                      >
                        Enviar mensaje
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-orange-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-text-primary font-bold mb-1">Zona de servicio</h3>
                      <p className="text-text-secondary text-lg">
                        Badalona y Barcelona
                      </p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-orange-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-text-primary font-bold mb-1">Horario</h3>
                      <p className="text-text-secondary text-lg">
                        Consultar
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-4">
                  <a
                    href="tel:+34629501941"
                    className="block w-full px-8 py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 font-bold text-center whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Llamar ahora
                  </a>
                  <a
                    href="https://wa.me/34629501941"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-8 py-4 border-2 border-orange-primary text-orange-primary rounded-full hover:bg-orange-primary hover:text-white transition-all duration-300 font-bold text-center whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    Enviar WhatsApp
                  </a>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={formInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
                  <h2 className="text-2xl font-extrabold text-text-primary mb-6">
                    Formulario de contacto
                  </h2>

                  <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                    <div className="space-y-5">
                      {/* Name */}
                      <div>
                        <label htmlFor="nombre" className="block text-text-primary font-medium mb-2 text-sm">
                          Nombre completo *
                        </label>
                        <input
                          type="text"
                          id="nombre"
                          name="nombre"
                          required
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 text-sm"
                          placeholder="Tu nombre"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label htmlFor="telefono" className="block text-text-primary font-medium mb-2 text-sm">
                          Teléfono *
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          required
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 text-sm"
                          placeholder="+34 600 000 000"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-text-primary font-medium mb-2 text-sm">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 text-sm"
                          placeholder="tu@email.com"
                        />
                      </div>

                      {/* Project Type */}
                      <div>
                        <label htmlFor="tipo-proyecto" className="block text-text-primary font-medium mb-2 text-sm">
                          Tipo de proyecto *
                        </label>
                        <select
                          id="tipo-proyecto"
                          name="tipo-proyecto"
                          required
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 text-sm"
                        >
                          <option value="">Selecciona una opción</option>
                          <option value="Tabiques">Tabiques de pladur</option>
                          <option value="Trasdosados">Trasdosados</option>
                          <option value="Falsos techos">Falsos techos</option>
                          <option value="Aislamiento acústico">Aislamiento acústico</option>
                          <option value="Aislamiento térmico">Aislamiento térmico</option>
                          <option value="Pladur antihumedad">Pladur antihumedad</option>
                          <option value="Reforma integral">Reforma integral</option>
                          <option value="Otro">Otro</option>
                        </select>
                      </div>

                      {/* Approximate m2 */}
                      <div>
                        <label htmlFor="metros" className="block text-text-primary font-medium mb-2 text-sm">
                          Metros cuadrados aproximados
                        </label>
                        <input
                          type="text"
                          id="metros"
                          name="metros"
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 text-sm"
                          placeholder="Ej: 20 m²"
                        />
                      </div>

                      {/* Neighborhood */}
                      <div>
                        <label htmlFor="barrio" className="block text-text-primary font-medium mb-2 text-sm">
                          Barrio o zona
                        </label>
                        <input
                          type="text"
                          id="barrio"
                          name="barrio"
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 text-sm"
                          placeholder="Ej: Centre, Gorg, Llefià..."
                        />
                      </div>

                      {/* Message */}
                      <div>
                        <label htmlFor="mensaje" className="block text-text-primary font-medium mb-2 text-sm">
                          Mensaje
                        </label>
                        <textarea
                          id="mensaje"
                          name="mensaje"
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none transition-colors duration-300 resize-none text-sm"
                          placeholder="Cuéntanos más sobre tu proyecto (máximo 500 caracteres)"
                        ></textarea>
                        <p className="text-text-muted text-xs mt-1">Máximo 500 caracteres</p>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-8 py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
                      </button>

                      {/* Status Messages */}
                      {submitStatus === 'success' && (
                        <div className="p-4 bg-green-500/10 border border-green-500 rounded-lg">
                          <p className="text-green-500 text-sm text-center">
                            ¡Mensaje enviado correctamente! Te contactaremos pronto.
                          </p>
                        </div>
                      )}
                      {submitStatus === 'error' && (
                        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg">
                          <p className="text-red-500 text-sm text-center">
                            Error al enviar el mensaje. Por favor, inténtalo de nuevo o llámanos directamente.
                          </p>
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 px-6 bg-dark-card">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-extrabold text-text-primary mb-8 text-center">
                Zona de servicio
              </h2>
              <div className="rounded-2xl overflow-hidden border border-dark-border h-[450px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47859.89693093!2d2.2170!3d41.4502!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a2f4b5d5d5d5%3A0x5d5d5d5d5d5d5d5d!2sBadalona%2C%20Barcelona!5e0!3m2!1ses!2ses!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Badalona"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default ContactoPage;
